import { useState } from "react";
import {
  Typography,
  Card,
  Upload,
  Alert,
  Space,
  Row,
  Col,
  message,
  Spin,
  Statistic,
  Modal,
  Tabs,
} from "antd";
import { ArrowRightOutlined, InboxOutlined } from "@ant-design/icons";
import * as XLSX from "xlsx-js-style";
import { PROJECT_HEALTH_IMPORT_API_URL, PROJECT_IMPORT_API_URL } from "../config/constants";
import { ExcelTemplate } from "../components/DataImport/ExcelTemplate";
import { apiFetch } from "../utils/apiClient";
 
const { Title, Text, Paragraph } = Typography;
const { Dragger } = Upload;
 
function isImportApiResponse(value) {
  if (!value || typeof value !== "object") {
    return false;
  }
 
  const response = value;
  return (
    typeof response.message === "string" &&
    typeof response.totalRowsProcessed === "number" &&
    typeof response.processedRecordsCount === "number" &&
    typeof response.failedRecordsCount === "number"
  );
}
 
const importApiUrl = PROJECT_IMPORT_API_URL;
const financialSummaryImportApiUrl = `${PROJECT_IMPORT_API_URL}/financial-summary`;
const projectHealthImportApiUrl = PROJECT_HEALTH_IMPORT_API_URL;
const ALLOWED_IMPORT_EXTENSIONS = [".xlsx", ".xls"];
 
const hasAllowedImportExtension = (fileName) => {
  const lower = fileName.toLowerCase();
  return ALLOWED_IMPORT_EXTENSIONS.some((ext) => lower.endsWith(ext));
};
 
const getAuthToken = () => {
  const token = sessionStorage.getItem("token") || sessionStorage.getItem("authToken");
 
  if (!token) {
    throw new Error("Authentication token not found. Please log in again.");
  }
 
  return token;
};
 
export function ProjectSetupImport() {
  const [uploading, setUploading] = useState(false);
  const [fileName, setFileName] = useState("");
  const [uploadResponse, setUploadResponse] = useState(null);
  const [uploadError, setUploadError] = useState("");
  const [failedErrorsOpen, setFailedErrorsOpen] = useState(false);
 
  const [financialUploading, setFinancialUploading] = useState(false);
  const [financialFileName, setFinancialFileName] = useState("");
  const [financialUploadResponse, setFinancialUploadResponse] = useState(null);
  const [financialUploadError, setFinancialUploadError] = useState("");
  const [financialFailedErrorsOpen, setFinancialFailedErrorsOpen] = useState(false);

  const [projectHealthUploading, setProjectHealthUploading] = useState(false);
  const [projectHealthFileName, setProjectHealthFileName] = useState("");
  const [projectHealthUploadResponse, setProjectHealthUploadResponse] = useState(null);
  const [projectHealthUploadError, setProjectHealthUploadError] = useState("");
  const [projectHealthFailedErrorsOpen, setProjectHealthFailedErrorsOpen] = useState(false);
 
  const handleUpload = async (file) => {
    if (uploading) {
      return false;
    }
 
    if (!hasAllowedImportExtension(file.name)) {
      const errorMessage = "Unsupported file type. Please upload .xlsx or .xls file.";
      setUploadError(errorMessage);
      message.error(errorMessage);
      return false;
    }
 
    setFileName(file.name);
    setUploadError("");
    setUploadResponse(null);
    setFailedErrorsOpen(false);
    setUploading(true);
 
    let token = "";
    try {
      token = getAuthToken();
    } catch {
      const errorMessage = "Authentication token not found. Please log in again.";
      setUploadError(errorMessage);
      message.error(errorMessage);
      setUploading(false);
      return false;
    }
 
    const formData = new FormData();
    formData.append("file", file);
 
    try {
      const response = await apiFetch(importApiUrl, {
        method: "POST",
        body: formData,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }, "Upload failed", { passthrough: [401, 403] });
 
      const contentType = response.headers.get("content-type") || "";
      const responseBody = contentType.includes("application/json")
        ? await response.json()
        : await response.text();
 
      if (!response.ok) {
        if (response.status === 401) {
          throw new Error("Unauthorized. Please log in again.");
        }
        if (response.status === 403) {
          throw new Error("You don't have permission to import projects.");
        }
 
        const errorText =
          typeof responseBody === "string"
            ? responseBody
            : responseBody?.message || responseBody?.error || JSON.stringify(responseBody);
 
        throw new Error(errorText || "Upload failed");
      }
 
      setUploadResponse(responseBody);
      if (isImportApiResponse(responseBody)) {
        if (responseBody.failedRecordsCount > 0) {
          message.warning(`Import completed with ${responseBody.failedRecordsCount} failed record(s).`);
        } else {
          message.success("Project import completed successfully.");
        }
      } else {
        message.success("Project import completed successfully.");
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Upload failed";
      setUploadError(errorMessage);
      message.error(errorMessage);
    } finally {
      setUploading(false);
    }
 
    return false;
  };
 
  const handleFinancialSummaryUpload = async (file) => {
    if (financialUploading) {
      return false;
    }
 
    if (!hasAllowedImportExtension(file.name)) {
      const errorMessage = "Unsupported file type. Please upload .xlsx or .xls file.";
      setFinancialUploadError(errorMessage);
      message.error(errorMessage);
      return false;
    }
 
    setFinancialFileName(file.name);
    setFinancialUploadError("");
    setFinancialUploadResponse(null);
    setFinancialFailedErrorsOpen(false);
    setFinancialUploading(true);
 
    let token = "";
    try {
      token = getAuthToken();
    } catch {
      const errorMessage = "Authentication token not found. Please log in again.";
      setFinancialUploadError(errorMessage);
      message.error(errorMessage);
      setFinancialUploading(false);
      return false;
    }
 
    const formData = new FormData();
    formData.append("file", file);
 
    try {
      const response = await apiFetch(financialSummaryImportApiUrl, {
        method: "POST",
        body: formData,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }, "Financial summary upload failed", { passthrough: [401, 403] });
 
      const contentType = response.headers.get("content-type") || "";
      const responseBody = contentType.includes("application/json")
        ? await response.json()
        : await response.text();
 
      if (!response.ok) {
        if (response.status === 401) {
          throw new Error("Unauthorized. Please log in again.");
        }
        if (response.status === 403) {
          throw new Error("You don't have permission to import financial summary.");
        }
 
        const errorText =
          typeof responseBody === "string"
            ? responseBody
            : responseBody?.message || responseBody?.error || JSON.stringify(responseBody);
 
        throw new Error(errorText || "Financial summary upload failed");
      }
 
      setFinancialUploadResponse(responseBody);
      if (isImportApiResponse(responseBody)) {
        if (responseBody.failedRecordsCount > 0) {
          message.warning(`Import completed with ${responseBody.failedRecordsCount} failed record(s).`);
        } else {
          message.success("Financial summary import completed successfully.");
        }
      } else {
        message.success("Financial summary import completed successfully.");
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Financial summary upload failed";
      setFinancialUploadError(errorMessage);
      message.error(errorMessage);
    } finally {
      setFinancialUploading(false);
    }
 
    return false;
  };

  const handleProjectHealthUpload = async (file) => {
    if (projectHealthUploading) {
      return false;
    }

    if (!hasAllowedImportExtension(file.name)) {
      const errorMessage = "Unsupported file type. Please upload .xlsx or .xls file.";
      setProjectHealthUploadError(errorMessage);
      message.error(errorMessage);
      return false;
    }

    setProjectHealthFileName(file.name);
    setProjectHealthUploadError("");
    setProjectHealthUploadResponse(null);
    setProjectHealthFailedErrorsOpen(false);
    setProjectHealthUploading(true);

    let token = "";
    try {
      token = getAuthToken();
    } catch {
      const errorMessage = "Authentication token not found. Please log in again.";
      setProjectHealthUploadError(errorMessage);
      message.error(errorMessage);
      setProjectHealthUploading(false);
      return false;
    }

    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await apiFetch(projectHealthImportApiUrl, {
        method: "POST",
        body: formData,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }, "Project health upload failed", { passthrough: [401, 403] });

      const contentType = response.headers.get("content-type") || "";
      const responseBody = contentType.includes("application/json")
        ? await response.json()
        : await response.text();

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error("Unauthorized. Please log in again.");
        }
        if (response.status === 403) {
          throw new Error("You don't have permission to import project health data.");
        }

        const errorText =
          typeof responseBody === "string"
            ? responseBody
            : responseBody?.message || responseBody?.error || JSON.stringify(responseBody);

        throw new Error(errorText || "Project health upload failed");
      }

      setProjectHealthUploadResponse(responseBody);
      if (isImportApiResponse(responseBody)) {
        if (responseBody.failedRecordsCount > 0) {
          message.warning(`Import completed with ${responseBody.failedRecordsCount} failed record(s).`);
        } else {
          message.success("Project health dashboard import completed successfully.");
        }
      } else {
        message.success("Project health dashboard import completed successfully.");
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Project health upload failed";
      setProjectHealthUploadError(errorMessage);
      message.error(errorMessage);
    } finally {
      setProjectHealthUploading(false);
    }

    return false;
  };
 
  const parsedResponse = isImportApiResponse(uploadResponse) ? uploadResponse : null;
  const hasRowErrors = (parsedResponse?.rowErrors?.length || 0) > 0;
  const parsedFinancialResponse = isImportApiResponse(financialUploadResponse)
    ? financialUploadResponse
    : null;
  const hasFinancialRowErrors = (parsedFinancialResponse?.rowErrors?.length || 0) > 0;

  const parsedProjectHealthResponse = isImportApiResponse(projectHealthUploadResponse)
    ? projectHealthUploadResponse
    : null;
  const hasProjectHealthRowErrors = (parsedProjectHealthResponse?.rowErrors?.length || 0) > 0;
 
  const handleDownloadTemplate = () => {
    const headers = [
      "Customer Name",
      "Project ID",
      "Project Description",
      "Contract Category",
      "Project Start Date",
      "Project End Date",
    ];
 
    const sampleRows = [[
      "Acme Corp",
      "PRJ-1001",
      "CRM modernization and workflow automation",
      "Time & Material",
      "01-04-2025",
      "01-05-2025",
    ]];

    generateAndDownloadExcel1(headers, sampleRows, "project_template.xlsx", "Project template downloaded.");
  };

  const generateAndDownloadExcel1 = (
  headers,
  sampleRows,
  fileName,
  successMessage
) => {
  try {
    const workbook = XLSX.utils.book_new();

    /*
     * =========================================================
     * COLORS
     * =========================================================
     */

    const COLORS = {
      header: "D9EAF7", // Pale Blue
      white: "FFFFFF",
      black: "000000",
      border: "000000",
    };

    /*
     * =========================================================
     * BORDER
     * =========================================================
     */

    const border = {
      top: {
        style: "thin",
        color: {
          rgb: COLORS.border,
        },
      },
      bottom: {
        style: "thin",
        color: {
          rgb: COLORS.border,
        },
      },
      left: {
        style: "thin",
        color: {
          rgb: COLORS.border,
        },
      },
      right: {
        style: "thin",
        color: {
          rgb: COLORS.border,
        },
      },
    };

    /*
     * =========================================================
     * WORKSHEET DATA
     * =========================================================
     */

    const worksheetData = [
      headers,
      ...sampleRows,
    ];

    const worksheet = XLSX.utils.aoa_to_sheet(
      worksheetData
    );

    /*
     * =========================================================
     * HEADER STYLE
     * =========================================================
     */

    const headerStyle = {
      fill: {
        patternType: "solid",
        fgColor: {
          rgb: COLORS.header,
        },
      },

      font: {
        bold: true,
        color: {
          rgb: COLORS.black,
        },
        sz: 11,
      },

      alignment: {
        horizontal: "center",
        vertical: "center",
        wrapText: true,
      },

      border,
    };

    /*
     * =========================================================
     * DATA STYLE
     * =========================================================
     */

    const dataStyle = {
      font: {
        color: {
          rgb: COLORS.black,
        },
        sz: 11,
      },

      alignment: {
        horizontal: "left",
        vertical: "center",
        wrapText: true,
      },

      border,
    };

    /*
     * =========================================================
     * APPLY HEADER STYLE
     * =========================================================
     */

    headers.forEach((header, colIndex) => {
      const address = XLSX.utils.encode_cell({
        r: 0,
        c: colIndex,
      });

      if (!worksheet[address]) {
        worksheet[address] = {
          t: "s",
          v: header,
        };
      }

      worksheet[address].s = headerStyle;
    });

    /*
     * =========================================================
     * APPLY DATA STYLE
     * =========================================================
     */

    sampleRows.forEach((row, rowIndex) => {
      row.forEach((value, colIndex) => {
        const address = XLSX.utils.encode_cell({
          r: rowIndex + 1,
          c: colIndex,
        });

        if (!worksheet[address]) {
          worksheet[address] = {
            t: "s",
            v: value ?? "",
          };
        }

        worksheet[address].s = dataStyle;
      });
    });

    /*
     * =========================================================
     * DATE COLUMN ALIGNMENT
     * =========================================================
     *
     * Project Start Date = column E
     * Project End Date   = column F
     */

    sampleRows.forEach((row, rowIndex) => {
      const excelRow = rowIndex + 1;

      [4, 5].forEach((colIndex) => {
        const address = XLSX.utils.encode_cell({
          r: excelRow,
          c: colIndex,
        });

        if (worksheet[address]) {
          worksheet[address].s = {
            ...worksheet[address].s,

            alignment: {
              horizontal: "center",
              vertical: "center",
              wrapText: true,
            },

            border,
          };
        }
      });
    });

    /*
     * =========================================================
     * ENSURE BORDER ON EVERY CELL
     * =========================================================
     */

    const range = XLSX.utils.decode_range(
      worksheet["!ref"] || "A1:F1"
    );

    for (
      let row = range.s.r;
      row <= range.e.r;
      row++
    ) {
      for (
        let col = range.s.c;
        col <= range.e.c;
        col++
      ) {
        const address = XLSX.utils.encode_cell({
          r: row,
          c: col,
        });

        if (!worksheet[address]) {
          worksheet[address] = {
            t: "s",
            v: "",
          };
        }

        worksheet[address].s = {
          ...(worksheet[address].s || {}),
          border,
        };
      }
    }

    /*
     * =========================================================
     * COLUMN WIDTHS
     * =========================================================
     */

    worksheet["!cols"] = [
      {
        wch: 28,
      }, // Customer Name

      {
        wch: 18,
      }, // Project ID

      {
        wch: 48,
      }, // Project Description

      {
        wch: 25,
      }, // Contract Category

      {
        wch: 22,
      }, // Project Start Date

      {
        wch: 22,
      }, // Project End Date
    ];

    /*
     * =========================================================
     * ROW HEIGHTS
     * =========================================================
     */

    worksheet["!rows"] = [
      {
        hpt: 32,
      }, // Header
    ];

    /*
     * =========================================================
     * FREEZE HEADER
     * =========================================================
     */

    worksheet["!freeze"] = {
      xSplit: 0,
      ySplit: 1,
      topLeftCell: "A2",
      activePane: "bottomRight",
    };

    /*
     * =========================================================
     * AUTO FILTER
     * =========================================================
     */

    worksheet["!autofilter"] = {
      ref: `A1:${XLSX.utils.encode_col(
        headers.length - 1
      )}1`,
    };

    /*
     * =========================================================
     * PAGE SETUP
     * =========================================================
     */

    worksheet["!pageSetup"] = {
      orientation: "landscape",
      fitToWidth: 1,
      fitToHeight: 0,
    };

    /*
     * =========================================================
     * APPEND SHEET
     * =========================================================
     */

    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      "Project Template"
    );

    /*
     * =========================================================
     * DOWNLOAD
     * =========================================================
     */

    XLSX.writeFile(
      workbook,
      fileName
    );

    message.success(
      successMessage
    );
  } catch (error) {
    const errorMessage =
      error instanceof Error
        ? error.message
        : "Failed to generate Excel file";

    message.error(
      errorMessage
    );
  }
};
  const handleFinancialTemplate = () => {
    const headers = [
      "PROJECT ID",
      "Revenue $",
      "EBITDA $",
      "Gross Margin $",
    ];
 
    const sampleRows = [[
      "WALM-ACD-1001",
      "250",
      "50",
      "120",
    ]];

    generateAndDownloadExcel1(headers, sampleRows, "financial_template.xlsx", "Financial template downloaded.");
  };

const handleProjectHealthTemplate = () => {
  const headers = [
    "Type",
    "Portfolio",
    "PROJECT ID",
    "Project / SCRUM team Name",
    "Project Type",
    "DM",
    "PM",
    "PMO",
    "PM rating",
    "Scope",
    "Schedule",
    "Quality",
    "Risk/Issue",
    "Resource",
    "Invoice Status 17th July",
    "EBITDA",
    "Support Needed - WM",
    "Support Needed - UST",
    "Support Needed - NA - Not Available",
    "Date to Green",
    "Item Need Attention",
    "Action Items",
    "Matured Practices",
    "Review Date",
  ];

  const sampleRows = [
    [
      "Project Review",
      "Advanced Customer Delivery",
      "WALM-ACD-1001",
      "ACD Modernization Project",
      "Managed Capacity",
      "Delivery Manager",
      "Project Manager",
      "1",
      "4",
      "G",
      "G",
      "G",
      "G",
      "G",
      "A",
      "22%",
      "",
      "2",
      "",
      "",
      "PCR Approval",
      "Invoiced till April 2026. May 2026.",
      "1. Created Wibey skills for PP",
      "2026-07-17",
    ]
  ];

  generateAndDownloadExcel(
    headers,
    sampleRows,
    "project_health_template.xlsx",
    "Project health template downloaded."
  );
};

const generateAndDownloadExcel = (
  headers,
  sampleRows,
  fileName,
  successMessage
) => {
  try {
    const workbook = XLSX.utils.book_new();

    /*
     * =========================================================
     * COLORS
     * =========================================================
     */

    const COLORS = {
      title: "C6E7F5",
      weekEnding: "A9C2CC",

      // Parent header colors
      type: "66B36B",
      projectDetails: "66B36B",
      overallRating: "1478A8",
      projectHealth: "1478A8",
      supportNeeded: "1478A8",
      dateToGreen: "66B36B",
      itemAttention: "66B36B",
      actionItems: "66B36B",
      maturedPractices: "66B36B",
      reviewDate: "66B36B",

      // Child header colors
      childBlue: "7FB5C9",
      childGreen: "66B36B",
      childBrown: "C08A6C",

      // Status colors
      green: "00b050",
      amber: "fabb02",
      red: "ff0000",

      white: "FFFFFF",
      black: "000000",
      border: "000000",
    };

    /*
     * =========================================================
     * TOP ROWS
     * =========================================================
     */

    const titleRow = [
      "Project Health Dashboard",
      ...Array(headers.length - 1).fill(""),
    ];

    const weekEndingRow = [
      "WeekEnding: 17-Jul-26",
      ...Array(headers.length - 1).fill(""),
    ];

    const separatorRow = Array(headers.length).fill("");

    /*
     * =========================================================
     * PARENT HEADER ROW
     * =========================================================
     */

    const parentHeaderRow = [
      "Type",
      "Portfolio",
      "Project ID",
      "Project / SCRUM team Name",
      "Project Type",
      "DM",
      "PM",

      "Overall rating",
      "",

      "Project Health",
      "",
      "",
      "",
      "",
      "",
      "",

      "Support Needed",
      "",
      "",

      "Date to Green",
      "Item Need Attention",
      "Action Items",
      "Matured Practices",
      "Review Date",
    ];

    /*
     * =========================================================
     * CHILD HEADER ROW
     * =========================================================
     */

    const childHeaderRow = [
      "",
      "",
      "",
      "",
      "",
      "",
      "",

      "PMO",
      "PM rating",

      "Scope",
      "Schedule",
      "Quality",
      "Risk/Issue",
      "Resource",
      "Invoice Status 17th July",
      "EBITDA",

      "WM",
      "UST",
      "NA - Not Available",

      "",
      "",
      "",
      "",
      "",
    ];

    /*
     * =========================================================
     * WORKSHEET DATA
     * =========================================================
     */

    const worksheetData = [
      titleRow,
      weekEndingRow,
      separatorRow,
      parentHeaderRow,
      childHeaderRow,
      ...sampleRows,
    ];

    const worksheet = XLSX.utils.aoa_to_sheet(
      worksheetData
    );

    /*
     * =========================================================
     * MERGED CELLS
     * =========================================================
     */

    worksheet["!merges"] = [
      // Title
      {
        s: { r: 0, c: 0 },
        e: { r: 0, c: 23 },
      },

      // Week Ending
      {
        s: { r: 1, c: 0 },
        e: { r: 1, c: 23 },
      },

      // Project Details - vertically merged
      {
        s: { r: 3, c: 0 },
        e: { r: 4, c: 0 },
      },
      {
        s: { r: 3, c: 1 },
        e: { r: 4, c: 1 },
      },
      {
        s: { r: 3, c: 2 },
        e: { r: 4, c: 2 },
      },
      {
        s: { r: 3, c: 3 },
        e: { r: 4, c: 3 },
      },
      {
        s: { r: 3, c: 4 },
        e: { r: 4, c: 4 },
      },
      {
        s: { r: 3, c: 5 },
        e: { r: 4, c: 5 },
      },
      {
        s: { r: 3, c: 6 },
        e: { r: 4, c: 6 },
      },

      // Overall Rating
      {
        s: { r: 3, c: 7 },
        e: { r: 3, c: 8 },
      },

      // Project Health
      {
        s: { r: 3, c: 9 },
        e: { r: 3, c: 15 },
      },

      // Support Needed
      {
        s: { r: 3, c: 16 },
        e: { r: 3, c: 18 },
      },

      // Remaining vertically merged columns
      {
        s: { r: 3, c: 19 },
        e: { r: 4, c: 19 },
      },
      {
        s: { r: 3, c: 20 },
        e: { r: 4, c: 20 },
      },
      {
        s: { r: 3, c: 21 },
        e: { r: 4, c: 21 },
      },
      {
        s: { r: 3, c: 22 },
        e: { r: 4, c: 22 },
      },
      {
        s: { r: 3, c: 23 },
        e: { r: 4, c: 23 },
      },
    ];

    /*
     * =========================================================
     * COMMON BORDER
     * =========================================================
     */

    const border = {
      top: {
        style: "thin",
        color: {
          rgb: COLORS.border,
        },
      },
      bottom: {
        style: "thin",
        color: {
          rgb: COLORS.border,
        },
      },
      left: {
        style: "thin",
        color: {
          rgb: COLORS.border,
        },
      },
      right: {
        style: "thin",
        color: {
          rgb: COLORS.border,
        },
      },
    };

    /*
     * =========================================================
     * TITLE STYLE
     * =========================================================
     */

    const titleStyle = {
      fill: {
        patternType: "solid",
        fgColor: {
          rgb: COLORS.title,
        },
      },
      font: {
        bold: true,
        color: {
          rgb: COLORS.black,
        },
        sz: 14,
      },
      alignment: {
        horizontal: "left",
        vertical: "center",
      },
      border,
    };

    /*
     * =========================================================
     * WEEK ENDING STYLE
     * =========================================================
     */

    const weekEndingStyle = {
      fill: {
        patternType: "solid",
        fgColor: {
          rgb: COLORS.weekEnding,
        },
      },
      font: {
        bold: true,
        color: {
          rgb: COLORS.black,
        },
        sz: 12,
      },
      alignment: {
        horizontal: "left",
        vertical: "center",
      },
      border,
    };

    /*
     * =========================================================
     * PARENT HEADER STYLE
     * =========================================================
     */

    const createParentStyle = (
      background,
      fontColor = COLORS.white
    ) => ({
      fill: {
        patternType: "solid",
        fgColor: {
          rgb: background,
        },
      },
      font: {
        bold: true,
        color: {
          rgb: fontColor,
        },
        sz: 11,
      },
      alignment: {
        horizontal: "center",
        vertical: "center",
        wrapText: true,
      },
      border,
    });

    /*
     * =========================================================
     * CHILD HEADER STYLE
     * =========================================================
     */

    const createChildStyle = (
      background,
      fontColor = COLORS.black
    ) => ({
      fill: {
        patternType: "solid",
        fgColor: {
          rgb: background,
        },
      },
      font: {
        bold: true,
        color: {
          rgb: fontColor,
        },
        sz: 10,
      },
      alignment: {
        horizontal: "center",
        vertical: "center",
        wrapText: true,
      },
      border,
    });

    /*
     * =========================================================
     * APPLY TITLE / WEEK ENDING STYLE
     * =========================================================
     */

    worksheet["A1"].s = titleStyle;
    worksheet["A2"].s = weekEndingStyle;

    /*
     * =========================================================
     * APPLY PARENT HEADER STYLES
     * =========================================================
     */

    const parentStyles= {
      // Project Details
      0: createParentStyle(COLORS.projectDetails),
      1: createParentStyle(COLORS.projectDetails),
      2: createParentStyle(COLORS.projectDetails),
      3: createParentStyle(COLORS.projectDetails),
      4: createParentStyle(COLORS.projectDetails),
      5: createParentStyle(COLORS.projectDetails),
      6: createParentStyle(COLORS.projectDetails),

      // Overall Rating
      7: createParentStyle(COLORS.overallRating),
      8: createParentStyle(COLORS.overallRating),

      // Project Health
      9: createParentStyle(COLORS.projectHealth),
      10: createParentStyle(COLORS.projectHealth),
      11: createParentStyle(COLORS.projectHealth),
      12: createParentStyle(COLORS.projectHealth),
      13: createParentStyle(COLORS.projectHealth),
      14: createParentStyle(COLORS.projectHealth),
      15: createParentStyle(COLORS.projectHealth),

      // Support Needed
      16: createParentStyle(COLORS.supportNeeded),
      17: createParentStyle(COLORS.supportNeeded),
      18: createParentStyle(COLORS.supportNeeded),

      // Remaining
      19: createParentStyle(COLORS.dateToGreen),
      20: createParentStyle(COLORS.itemAttention),
      21: createParentStyle(COLORS.actionItems),
      22: createParentStyle(COLORS.maturedPractices),
      23: createParentStyle(COLORS.reviewDate),
    };

    for (let col = 0; col < headers.length; col++) {
      const cellAddress = XLSX.utils.encode_cell({
        r: 3,
        c: col,
      });

      if (worksheet[cellAddress]) {
        worksheet[cellAddress].s =
          parentStyles[col];
      }
    }

    /*
     * =========================================================
     * APPLY CHILD HEADER STYLES
     *
     * All Project Health + Support Needed children
     * use the same BLUE color.
     * =========================================================
     */

    // Overall Rating children
    worksheet["H5"].s = createChildStyle(
      COLORS.childBlue
    );

    worksheet["I5"].s = createChildStyle(
      COLORS.childBlue
    );

    // Project Health children
    // Scope, Schedule, Quality, Risk/Issue,
    // Resource, Invoice Status, EBITDA
    for (let col = 9; col <= 15; col++) {
      const address = XLSX.utils.encode_cell({
        r: 4,
        c: col,
      });

      if (worksheet[address]) {
        worksheet[address].s = createChildStyle(
          COLORS.childBlue
        );
      }
    }

    // Support Needed children
    // WM, UST, NA - Not Available
    for (let col = 16; col <= 18; col++) {
      const address = XLSX.utils.encode_cell({
        r: 4,
        c: col,
      });

      if (worksheet[address]) {
        worksheet[address].s = createChildStyle(
          COLORS.childBlue
        );
      }
    }

    /*
     * =========================================================
     * VERTICAL HEADER ALIGNMENT
     * =========================================================
     */

    const verticalColumns = [
      7,  // PMO
      8,  // PM Rating
      9,  // Scope
      10, // Schedule
      11, // Quality
      12, // Risk/Issue
      13, // Resource
      14, // Invoice Status
      15, // EBITDA
      16, // WM
      17, // UST
      18, // NA
    ];

    verticalColumns.forEach((col) => {
      const address = XLSX.utils.encode_cell({
        r: 4,
        c: col,
      });

      if (worksheet[address]) {
        worksheet[address].s = {
          ...(worksheet[address].s || {}),
          alignment: {
            horizontal: "center",
            vertical: "center",
            wrapText: true,
            textRotation: 90,
          },
          border,
        };
      }
    });

    /*
     * =========================================================
     * DATA CELL STYLE
     * =========================================================
     */

    const defaultDataStyle = {
      font: {
        color: {
          rgb: COLORS.black,
        },
        sz: 11,
      },
      alignment: {
        vertical: "top",
        wrapText: true,
      },
      border,
    };

    /*
     * =========================================================
     * STATUS CELL STYLE
     * =========================================================
     */

    const getStatusStyle = (status) => {
      const value = String(status || "")
        .trim()
        .toUpperCase();

      let background = "";

      if (value === "G") {
        background = COLORS.green;
      } else if (value === "A") {
        background = COLORS.amber;
      } else if (value === "R") {
        background = COLORS.red;
      }

      if (!background) {
        return defaultDataStyle;
      }

      return {
        ...defaultDataStyle,

        fill: {
          patternType: "solid",
          fgColor: {
            rgb: background,
          },
        },

        font: {
          bold: true,
          color: {
            rgb:
              value === "R"
                ? COLORS.white
                : COLORS.black,
          },
          sz: 11,
        },

        alignment: {
          horizontal: "center",
          vertical: "center",
          wrapText: true,
        },

        border,
      };
    };

    /*
     * =========================================================
     * APPLY DATA STYLES
     * =========================================================
     */

    sampleRows.forEach((row, rowIndex) => {
      const excelRow = rowIndex + 5;

      row.forEach((value, colIndex) => {
        const address = XLSX.utils.encode_cell({
          r: excelRow,
          c: colIndex,
        });

        if (!worksheet[address]) {
          worksheet[address] = {
            t: "s",
            v: value ?? "",
          };
        }

        /*
         * Status columns:
         *
         * 9  = Scope
         * 10 = Schedule
         * 11 = Quality
         * 12 = Risk/Issue
         * 13 = Resource
         * 14 = Invoice Status
         */

        if (colIndex >= 9 && colIndex <= 14) {
          worksheet[address].s =
            getStatusStyle(
              String(value ?? "")
            );
        } else {
          worksheet[address].s =
            defaultDataStyle;
        }
      });
    });

    /*
     * =========================================================
     * COLUMN WIDTHS
     * =========================================================
     */

    worksheet["!cols"] = [
      { wch: 13 }, // Type
      { wch: 17 }, // Portfolio
      { wch: 18 }, // Project ID
      { wch: 20 }, // Project / Scrum
      { wch: 14 }, // Project Type
      { wch: 15 }, // DM
      { wch: 15 }, // PM
      { wch: 10 }, // PMO
      { wch: 10 }, // PM Rating
      { wch: 10 }, // Scope
      { wch: 10 }, // Schedule
      { wch: 10 }, // Quality
      { wch: 10 }, // Risk
      { wch: 10 }, // Resource
      { wch: 10 }, // Invoice
      { wch: 10 }, // EBITDA
      { wch: 10 }, // WM
      { wch: 10 }, // UST
      { wch: 10 }, // NA
      { wch: 10 }, // Date to Green
      { wch: 25 }, // Item Attention
      { wch: 25 }, // Action Items
      { wch: 25 }, // Matured Practices
      { wch: 16 }, // Review Date
    ];

    /*
     * =========================================================
     * ROW HEIGHTS
     * =========================================================
     */

    worksheet["!rows"] = [
      { hpt: 22 }, // Title
      { hpt: 20 }, // Week Ending
      { hpt: 8 }, // Separator
      { hpt: 20 }, // Parent header
      { hpt: 60 }, // Child header
    ];

    /*
     * =========================================================
     * APPLY BORDER TO EVERY CELL
     *
     * This ensures every column has:
     * - Top border
     * - Bottom border
     * - Left border
     * - Right border
     *
     * Also handles blank cells inside merged areas.
     * =========================================================
     */

    const range = XLSX.utils.decode_range(
      worksheet["!ref"] || "A1:X1"
    );

    for (
      let row = range.s.r;
      row <= range.e.r;
      row++
    ) {
      for (
        let col = range.s.c;
        col <= range.e.c;
        col++
      ) {
        const address = XLSX.utils.encode_cell({
          r: row,
          c: col,
        });

        /*
         * Create empty cell if it doesn't exist.
         * This is important for maintaining borders
         * around blank cells.
         */
        if (!worksheet[address]) {
          worksheet[address] = {
            t: "s",
            v: "",
          };
        }

        worksheet[address].s = {
          ...(worksheet[address].s || {}),
          border,
        };
      }
    }

    /*
     * =========================================================
     * FREEZE PANES
     * =========================================================
     */

    worksheet["!freeze"] = {
      xSplit: 0,
      ySplit: 5,
      topLeftCell: "A6",
      activePane: "bottomRight",
    };

    /*
     * =========================================================
     * AUTO FILTER
     * =========================================================
     */

    worksheet["!autofilter"] = {
      ref: "A5:X5",
    };

    /*
     * =========================================================
     * PAGE SETTINGS
     * =========================================================
     */

    worksheet["!pageSetup"] = {
      orientation: "landscape",
      fitToWidth: 1,
      fitToHeight: 0,
    };

    /*
     * =========================================================
     * APPEND SHEET
     * =========================================================
     */

    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      "Template"
    );

    /*
     * =========================================================
     * DOWNLOAD
     * =========================================================
     */

    XLSX.writeFile(
      workbook,
      fileName
    );

    message.success(
      successMessage
    );
  } catch (error) {
    const errorMessage =
      error instanceof Error
        ? error.message
        : "Failed to generate Excel file";

    message.error(
      errorMessage
    );
  }
};
  return (
    <div>

 
      <Tabs
        defaultActiveKey="projectImport"
        items={[
          {
            key: "projectImport",
            label: "Project Import",
            children: (
              <Row gutter={[20, 20]} align="stretch">
                <Col xs={24} lg={16} style={{ display: "flex" }}>
                  <Card
                    style={{
                      borderRadius: 12,
                      border: "1px solid #f0f0f0",
                      width: "100%",
                      height: "100%",
                    }}
                  >
                    <Dragger
                      accept=".xlsx,.xls"
                      beforeUpload={handleUpload}
                      showUploadList={false}
                      maxCount={1}
                      multiple={false}
                      disabled={uploading}
                      style={{ borderRadius: 8 }}
                    >
                      <div style={{ padding: "24px 0" }}>
                        <p>
                          <InboxOutlined style={{ fontSize: 48, color: "#1677ff" }} />
                        </p>
                        <p style={{ fontSize: 16, fontWeight: 600 }}>Click or drag Project Details excel file to upload</p>
                        <p style={{ color: "#8c8c8c" }}>Supports only .xlsx and .xls formats</p>
                      </div>
                    </Dragger>
 
                    {uploading && (
                      <div style={{ marginTop: 16 }}>
                        <Space>
                          <Spin size="small" />
                          <Text style={{ color: "#8c8c8c" }}>Uploading {fileName}...</Text>
                        </Space>
                      </div>
                    )}
 
                    {!uploading && uploadError && (
                      <Alert
                        style={{ marginTop: 16, borderRadius: 8 }}
                        type="error"
                        showIcon
                        message="Upload failed"
                        description={uploadError}
                      />
                    )}
 
                    {!uploading && uploadResponse !== null && (
                      <Card title="Import Result" size="small" style={{ marginTop: 16, borderRadius: 8 }}>
                        <Paragraph style={{ marginBottom: 12 }}>
                          Uploaded file: <Text strong>{fileName}</Text>
                        </Paragraph>
 
                        {parsedResponse ? (
                          <>
                            <Alert
                              showIcon
                              type={parsedResponse.failedRecordsCount > 0 ? "warning" : "success"}
                              message={parsedResponse.message}
                              style={{ borderRadius: 8, marginBottom: 12 }}
                            />
 
                            <Row gutter={[12, 12]}>
                              <Col xs={24} sm={8}>
                                <Card size="small" style={{ borderRadius: 8 }}>
                                  <Statistic title="Total Rows" value={parsedResponse.totalRowsProcessed} />
                                </Card>
                              </Col>
                              <Col xs={24} sm={8}>
                                <Card size="small" style={{ borderRadius: 8 }}>
                                  <Statistic
                                    title="Processed"
                                    value={parsedResponse.processedRecordsCount}
                                    valueStyle={{ color: "#3f8600" }}
                                  />
                                </Card>
                              </Col>
                              <Col xs={24} sm={8}>
                                <Card
                                  size="small"
                                  hoverable={parsedResponse.failedRecordsCount > 0}
                                  style={{ borderRadius: 8, position: "relative" }}
                                  bodyStyle={{
                                    cursor: parsedResponse.failedRecordsCount > 0 ? "pointer" : "default",
                                  }}
                                  onClick={() => {
                                    if (parsedResponse.failedRecordsCount > 0) {
                                      setFailedErrorsOpen(true);
                                    }
                                  }}
                                >
                                  {parsedResponse.failedRecordsCount > 0 && (
                                    <ArrowRightOutlined
                                      style={{
                                        position: "absolute",
                                        top: 12,
                                        right: 12,
                                        color: "#1677ff",
                                        fontSize: 14,
                                      }}
                                    />
                                  )}
 
                                  <Statistic
                                    title="Failed"
                                    value={parsedResponse.failedRecordsCount}
                                    valueStyle={{
                                      color: parsedResponse.failedRecordsCount > 0 ? "#cf1322" : "#3f8600",
                                    }}
                                  />
                                </Card>
                              </Col>
                            </Row>
                          </>
                        ) : (
                          <pre
                            style={{
                              margin: 0,
                              whiteSpace: "pre-wrap",
                              wordBreak: "break-word",
                              fontSize: 12,
                            }}
                          >
                            {typeof uploadResponse === "string"
                              ? uploadResponse
                              : JSON.stringify(uploadResponse, null, 2)}
                          </pre>
                        )}
                      </Card>
                    )}
 
                    <Modal
                      title="Failed Row Errors"
                      open={failedErrorsOpen}
                      onCancel={() => setFailedErrorsOpen(false)}
                      footer={null}
                      width={700}
                    >
                      <div style={{ maxHeight: "60vh", overflowY: "auto", paddingRight: 4 }}>
                        {parsedResponse ? (
                          hasRowErrors ? (
                            <Space vertical style={{ width: "100%" }} size={12}>
                              {parsedResponse.rowErrors?.map((rowError) => (
                                <Alert
                                  key={`${rowError.rowNumber}-${rowError.projectId ?? "NA"}`}
                                  type="error"
                                  showIcon
                                  style={{ borderRadius: 8 }}
                                  message={`Row ${rowError.rowNumber} | Project ID: ${rowError.projectId ?? "N/A"}`}
                                  description={rowError.errors.join(" ")}
                                />
                              ))}
                            </Space>
                          ) : (
                            <Alert
                              type="warning"
                              showIcon
                              style={{ borderRadius: 8 }}
                              message={`Failed records: ${parsedResponse.failedRecordsCount}`}
                              description="The API returned a failed count, but no row-level error details were included."
                            />
                          )
                        ) : (
                          <Alert
                            type="info"
                            showIcon
                            style={{ borderRadius: 8 }}
                            message="No import summary available"
                            description="Upload a file first to view failed row details."
                          />
                        )}
                      </div>
                    </Modal>
                  </Card>
                </Col>
 
                <Col xs={24} lg={8} style={{ display: "flex" }}>
                  <Card
                    style={{
                      borderRadius: 12,
                      border: "1px solid #f0f0f0",
                      width: "100%",
                      height: "100%",
                    }}
                    styles={{ body: { height: "100%" } }}
                  >
                    <ExcelTemplate
                      handleDownloadTemplate={handleDownloadTemplate}
                      templateType="Project"
                      requiredColumns="Customer Name, Project ID, Project Description,
                      Contract Category, Project Start Date, Project End Date."
                    />
                  </Card>
                </Col>
              </Row>
            ),
          },
          {
            key: "financialSummaryImport",
            label: "Financial Summary Import",
            children: (
              <Row gutter={[20, 20]} align="stretch">
                <Col xs={24} lg={16} style={{ display: "flex" }}>
                  <Card
                    style={{
                      borderRadius: 12,
                      border: "1px solid #f0f0f0",
                      width: "100%",
                      height: "100%",
                    }}
                  >
                    <Dragger
                      accept=".xlsx,.xls"
                      beforeUpload={handleFinancialSummaryUpload}
                      showUploadList={false}
                      maxCount={1}
                      multiple={false}
                      disabled={financialUploading}
                      style={{ borderRadius: 8 }}
                    >
                      <div style={{ padding: "24px 0" }}>
                        <p>
                          <InboxOutlined style={{ fontSize: 48, color: "#1677ff" }} />
                        </p>
                        <p style={{ fontSize: 16, fontWeight: 600 }}>
                          Click or drag  Financial Summary Excel file to upload
                        </p>
                        <p style={{ color: "#8c8c8c" }}>Supports only .xlsx and .xls formats</p>
                      </div>
                    </Dragger>
 
                    {financialUploading && (
                      <div style={{ marginTop: 16 }}>
                        <Space>
                          <Spin size="small" />
                          <Text style={{ color: "#8c8c8c" }}>Uploading {financialFileName}...</Text>
                        </Space>
                      </div>
                    )}
 
                    {!financialUploading && financialUploadError && (
                      <Alert
                        style={{ marginTop: 16, borderRadius: 8 }}
                        type="error"
                        showIcon
                        message="Upload failed"
                        description={financialUploadError}
                      />
                    )}
 
                    {!financialUploading && financialUploadResponse !== null && (
                      <Card title="Import Result" size="small" style={{ marginTop: 16, borderRadius: 8 }}>
                        <Paragraph style={{ marginBottom: 12 }}>
                          Uploaded file: <Text strong>{financialFileName}</Text>
                        </Paragraph>
 
                        {parsedFinancialResponse ? (
                          <>
                            <Alert
                              showIcon
                              type={parsedFinancialResponse.failedRecordsCount > 0 ? "warning" : "success"}
                              message={parsedFinancialResponse.message}
                              style={{ borderRadius: 8, marginBottom: 12 }}
                            />
 
                            <Row gutter={[12, 12]}>
                              <Col xs={24} sm={8}>
                                <Card size="small" style={{ borderRadius: 8 }}>
                                  <Statistic title="Total Rows" value={parsedFinancialResponse.totalRowsProcessed} />
                                </Card>
                              </Col>
                              <Col xs={24} sm={8}>
                                <Card size="small" style={{ borderRadius: 8 }}>
                                  <Statistic
                                    title="Processed"
                                    value={parsedFinancialResponse.processedRecordsCount}
                                    valueStyle={{ color: "#3f8600" }}
                                  />
                                </Card>
                              </Col>
                              <Col xs={24} sm={8}>
                                <Card
                                  size="small"
                                  hoverable={parsedFinancialResponse.failedRecordsCount > 0}
                                  style={{ borderRadius: 8, position: "relative" }}
                                  bodyStyle={{
                                    cursor: parsedFinancialResponse.failedRecordsCount > 0 ? "pointer" : "default",
                                  }}
                                  onClick={() => {
                                    if (parsedFinancialResponse.failedRecordsCount > 0) {
                                      setFinancialFailedErrorsOpen(true);
                                    }
                                  }}
                                >
                                  {parsedFinancialResponse.failedRecordsCount > 0 && (
                                    <ArrowRightOutlined
                                      style={{
                                        position: "absolute",
                                        top: 12,
                                        right: 12,
                                        color: "#1677ff",
                                        fontSize: 14,
                                      }}
                                    />
                                  )}
 
                                  <Statistic
                                    title="Failed"
                                    value={parsedFinancialResponse.failedRecordsCount}
                                    valueStyle={{
                                      color: parsedFinancialResponse.failedRecordsCount > 0 ? "#cf1322" : "#3f8600",
                                    }}
                                  />
                                </Card>
                              </Col>
                            </Row>
                          </>
                        ) : (
                          <pre
                            style={{
                              margin: 0,
                              whiteSpace: "pre-wrap",
                              wordBreak: "break-word",
                              fontSize: 12,
                            }}
                          >
                            {typeof financialUploadResponse === "string"
                              ? financialUploadResponse
                              : JSON.stringify(financialUploadResponse, null, 2)}
                          </pre>
                        )}
                      </Card>
                    )}
 
                    <Modal
                      title="Failed Row Errors"
                      open={financialFailedErrorsOpen}
                      onCancel={() => setFinancialFailedErrorsOpen(false)}
                      footer={null}
                      width={700}
                    >
                      <div style={{ maxHeight: "60vh", overflowY: "auto", paddingRight: 4 }}>
                        {parsedFinancialResponse ? (
                          hasFinancialRowErrors ? (
                            <Space vertical style={{ width: "100%" }} size={12}>
                              {parsedFinancialResponse.rowErrors?.map((rowError) => (
                                <Alert
                                  key={`${rowError.rowNumber}-${rowError.projectId ?? "NA"}`}
                                  type="error"
                                  showIcon
                                  style={{ borderRadius: 8 }}
                                  message={`Row ${rowError.rowNumber} | Project ID: ${rowError.projectId ?? "N/A"}`}
                                  description={rowError.errors.join(" ")}
                                />
                              ))}
                            </Space>
                          ) : (
                            <Alert
                              type="warning"
                              showIcon
                              style={{ borderRadius: 8 }}
                              message={`Failed records: ${parsedFinancialResponse.failedRecordsCount}`}
                              description="The API returned a failed count, but no row-level error details were included."
                            />
                          )
                        ) : (
                          <Alert
                            type="info"
                            showIcon
                            style={{ borderRadius: 8 }}
                            message="No import summary available"
                            description="Upload a file first to view failed row details."
                          />
                        )}
                      </div>
                    </Modal>
                  </Card>
                </Col>
                <Col xs={24} lg={8} style={{ display: "flex" }}>
                  <Card
                    style={{
                      borderRadius: 12,
                      border: "1px solid #f0f0f0",
                      width: "100%",
                      height: "100%",
                    }}
                    styles={{ body: { height: "100%" } }}
                  >
                    <ExcelTemplate
                      handleDownloadTemplate={handleFinancialTemplate}
                      templateType="Financial"
                      requiredColumns="Project ID,Revenue,EBITDA,Gross Margin"
                    />
                  </Card>
                </Col>

              </Row>
            ),
          },
          {
            key: "projectHealthImport",
            label: "Project Health Dashboard Import",
            children: (
              <Row gutter={[20, 20]} align="stretch">
                <Col xs={24} lg={16} style={{ display: "flex" }}>
                  <Card
                    style={{
                      borderRadius: 12,
                      border: "1px solid #f0f0f0",
                      width: "100%",
                      height: "100%",
                    }}
                  >
                    <Dragger
                      accept=".xlsx,.xls"
                      beforeUpload={handleProjectHealthUpload}
                      showUploadList={false}
                      maxCount={1}
                      multiple={false}
                      disabled={projectHealthUploading}
                      style={{ borderRadius: 8 }}
                    >
                      <div style={{ padding: "24px 0" }}>
                        <p>
                          <InboxOutlined style={{ fontSize: 48, color: "#1677ff" }} />
                        </p>
                        <p style={{ fontSize: 16, fontWeight: 600 }}>
                          Click or drag Project Health Dashboard excel file to upload
                        </p>
                        <p style={{ color: "#8c8c8c" }}>Supports only .xlsx and .xls formats</p>
                      </div>
                    </Dragger>

                    {projectHealthUploading && (
                      <div style={{ marginTop: 16 }}>
                        <Space>
                          <Spin size="small" />
                          <Text style={{ color: "#8c8c8c" }}>Uploading {projectHealthFileName}...</Text>
                        </Space>
                      </div>
                    )}

                    {!projectHealthUploading && projectHealthUploadError && (
                      <Alert
                        style={{ marginTop: 16, borderRadius: 8 }}
                        type="error"
                        showIcon
                        message="Upload failed"
                        description={projectHealthUploadError}
                      />
                    )}

                    {!projectHealthUploading && projectHealthUploadResponse !== null && (
                      <Card title="Import Result" size="small" style={{ marginTop: 16, borderRadius: 8 }}>
                        <Paragraph style={{ marginBottom: 12 }}>
                          Uploaded file: <Text strong>{projectHealthFileName}</Text>
                        </Paragraph>

                        {parsedProjectHealthResponse ? (
                          <>
                            <Alert
                              showIcon
                              type={parsedProjectHealthResponse.failedRecordsCount > 0 ? "warning" : "success"}
                              message={parsedProjectHealthResponse.message}
                              style={{ borderRadius: 8, marginBottom: 12 }}
                            />

                            <Row gutter={[12, 12]}>
                              <Col xs={24} sm={8}>
                                <Card size="small" style={{ borderRadius: 8 }}>
                                  <Statistic title="Total Rows" value={parsedProjectHealthResponse.totalRowsProcessed} />
                                </Card>
                              </Col>
                              <Col xs={24} sm={8}>
                                <Card size="small" style={{ borderRadius: 8 }}>
                                  <Statistic
                                    title="Processed"
                                    value={parsedProjectHealthResponse.processedRecordsCount}
                                    valueStyle={{ color: "#3f8600" }}
                                  />
                                </Card>
                              </Col>
                              <Col xs={24} sm={8}>
                                <Card
                                  size="small"
                                  hoverable={parsedProjectHealthResponse.failedRecordsCount > 0}
                                  style={{ borderRadius: 8, position: "relative" }}
                                  bodyStyle={{
                                    cursor: parsedProjectHealthResponse.failedRecordsCount > 0 ? "pointer" : "default",
                                  }}
                                  onClick={() => {
                                    if (parsedProjectHealthResponse.failedRecordsCount > 0) {
                                      setProjectHealthFailedErrorsOpen(true);
                                    }
                                  }}
                                >
                                  {parsedProjectHealthResponse.failedRecordsCount > 0 && (
                                    <ArrowRightOutlined
                                      style={{
                                        position: "absolute",
                                        top: 12,
                                        right: 12,
                                        color: "#1677ff",
                                        fontSize: 14,
                                      }}
                                    />
                                  )}

                                  <Statistic
                                    title="Failed"
                                    value={parsedProjectHealthResponse.failedRecordsCount}
                                    valueStyle={{
                                      color: parsedProjectHealthResponse.failedRecordsCount > 0 ? "#cf1322" : "#3f8600",
                                    }}
                                  />
                                </Card>
                              </Col>
                            </Row>
                          </>
                        ) : (
                          <pre
                            style={{
                              margin: 0,
                              whiteSpace: "pre-wrap",
                              wordBreak: "break-word",
                              fontSize: 12,
                            }}
                          >
                            {typeof projectHealthUploadResponse === "string"
                              ? projectHealthUploadResponse
                              : JSON.stringify(projectHealthUploadResponse, null, 2)}
                          </pre>
                        )}
                      </Card>
                    )}

                    <Modal
                      title="Failed Row Errors"
                      open={projectHealthFailedErrorsOpen}
                      onCancel={() => setProjectHealthFailedErrorsOpen(false)}
                      footer={null}
                      width={700}
                    >
                      <div style={{ maxHeight: "60vh", overflowY: "auto", paddingRight: 4 }}>
                        {parsedProjectHealthResponse ? (
                          hasProjectHealthRowErrors ? (
                            <Space vertical style={{ width: "100%" }} size={12}>
                              {parsedProjectHealthResponse.rowErrors?.map((rowError) => (
                                <Alert
                                  key={`${rowError.rowNumber}-${rowError.projectId ?? "NA"}`}
                                  type="error"
                                  showIcon
                                  style={{ borderRadius: 8 }}
                                  message={`Row ${rowError.rowNumber} | Project ID: ${rowError.projectId ?? "N/A"}`}
                                  description={rowError.errors.join(" ")}
                                />
                              ))}
                            </Space>
                          ) : (
                            <Alert
                              type="warning"
                              showIcon
                              style={{ borderRadius: 8 }}
                              message={`Failed records: ${parsedProjectHealthResponse.failedRecordsCount}`}
                              description="The API returned a failed count, but no row-level error details were included."
                            />
                          )
                        ) : (
                          <Alert
                            type="info"
                            showIcon
                            style={{ borderRadius: 8 }}
                            message="No import summary available"
                            description="Upload a file first to view failed row details."
                          />
                        )}
                      </div>
                    </Modal>
                  </Card>
                </Col>
                <Col xs={24} lg={8} style={{ display: "flex" }}>
                  <Card
                    style={{
                      borderRadius: 12,
                      border: "1px solid #f0f0f0",
                      width: "100%",
                      height: "100%",
                    }}
                    styles={{ body: { height: "100%" } }}
                  >
                    <ExcelTemplate
                      handleDownloadTemplate={handleProjectHealthTemplate}
                      templateType="Project Health"
                      requiredColumns="Project ID,Project ,Review Date"
                    />
                  </Card>
                </Col>
              </Row>
            ),
          },
        ]}
      />
    </div>
  );
}